import { getCurrencyCode } from '@arcteryx/js-price';
import * as Hash from 'hash.js';
import { getCustomer } from '@arcteryx/js-user-detection';
import { getForgeRockUser } from '@arcteryx/js-auth';

let dataLayerName = "appEventData";
let dataLayerContainer = null;
const getDataLayer = () => {
  const container = dataLayerContainer || window;
  if (!container[dataLayerName]) {
    container[dataLayerName] = [];
  }
  return container[dataLayerName];
};
const pushEvent = function (name) {
  let data = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  const payload = Object.assign({
    event: name
  }, data);
  getDataLayer().push(payload);
};
// If you don't want to use the `window` container object, provide something else.
const setContainer = obj => {
  dataLayerContainer = obj;
};
const setName = name => {
  dataLayerName = name;
};
const reset = () => {
  delete (dataLayerContainer || window)[dataLayerName];
  dataLayerName = "appEventData";
  dataLayerContainer = null;
};

const cleanse = function (obj) {
  let matchValue = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  return Object.keys(obj).filter(k => obj[k] !== matchValue).reduce((newObj, k) => typeof obj[k] === "object" && obj[k] !== null && !Array.isArray(obj[k]) ? Object.assign(Object.assign({}, newObj), {
    [k]: cleanse(obj[k], matchValue)
  }) : Object.assign(Object.assign({}, newObj), {
    [k]: obj[k]
  }),
  // Copy value.
  {});
};
// Provide a common interface to data via the `model` accessor.
class Data$1 {
  constructor() {
    let data = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    this._model = data;
  }
  get model() {
    return cleanse(this._model);
  }
}

// Return outlet if sale is used for market value
const adaptMarket = market => (market === null || market === void 0 ? void 0 : market.toLowerCase()) === "sale" ? "outlet" : market !== null && market !== void 0 ? market : "";
// 1:1 mappings of category to pageType
const cat2type = {
  home: "home-page",
  catalog: "list-page",
  product: "product-page",
  shop: "shop-page",
  account: "account-page",
  cart: "cart-page",
  checkout: "checkout-page",
  explore: "explore-page",
  help: "help-page",
  compare: "compare-page",
  careers: "careers-page"
};
const getPageCategory = category => category === "shop" || category === "compare" ? "catalog" : category;
class Page extends Data$1 {
  constructor(_ref) {
    let {
      market,
      country,
      language,
      category,
      name,
      type,
      subsection,
      subsection2,
      subsection3
    } = _ref;
    var _a;
    super();
    const siteLang = language === "jp" ? "ja" : language;
    const countryUpperCase = country.toUpperCase();
    const categoryValue = subsection && subsection2 ? `${subsection}-${subsection2}` : null;
    this._model = Object.assign(Object.assign({}, this._model), {
      pageCategory: getPageCategory(category),
      category: categoryValue,
      pageName: name,
      pageType: (_a = type !== null && type !== void 0 ? type : cat2type[category]) !== null && _a !== void 0 ? _a : null,
      pageSubtype: null,
      pageLocale: `${siteLang}_${countryUpperCase}`,
      siteCountry: countryUpperCase,
      siteLanguage: siteLang,
      subsection,
      subsection2,
      subsection3,
      brand: `arcteryx:${adaptMarket(market)}`
    });
    // Kind of a one-off in the spec: Not part of the page model, but part of the Page Loaded event
    this.currency = getCurrencyCode(country, true);
  }
  // generate a Page object from url. If url is null, use window.location.href
  static $createFromUrl() {
    let url = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
    let market = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : "";
    const urlObj = new URL(url !== null && url !== void 0 ? url : window.location.href);
    // Strip off first slash
    // Strip off last slash
    // Strip off market in case viewing a backend directly
    const path = urlObj.pathname.replace(/^\//, "").replace(/\/$/, "").replace(/^(leaf\/|outdoor\/|outlet\/|sale\/|urban\/)/, "");
    // Strip off use=, browse=, sub-cat= from subsection
    let [country, language, category, subsection, subsection2, subsection3] = path.split("/").map(item => item.replace(/.+=/, ""));
    // special cases:
    if (!category) {
      category = "home";
    } else if (category === "c") {
      category = "catalog";
    } else if (category === "shop") {
      if (subsection) {
        category = "product"; // we have a product slug so it's a PDP
      }
    }

    const name = [category, subsection, subsection2, subsection3].reduce((arr, item) => {
      if (item) {
        arr.push(item);
      }
      return arr;
    }, []).join("|");
    // assign sub-cat value to subsection2 for SWAT-8247
    const subcategory = urlObj.searchParams.get("sub-cat");
    subsection2 = subcategory !== null && subcategory !== void 0 ? subcategory : subsection2;
    // Spec doesn't want these for compare
    if (category === "compare") {
      subsection = subsection2 = subsection3 = undefined;
    }
    return new Page({
      market,
      country,
      language,
      category,
      name,
      subsection,
      subsection2,
      subsection3
    });
  }
}

// More info on which attributes are expected to be sent for each event.
// https://amersportsonline-my.sharepoint.com/:x:/g/personal/jamie_chong_amersports_com/EUIY5rLvMWRHqtmGxi2sKOYBH39RvyCtal4Xxpv-YMZ3IA?e=cIuQdD
const getPriceType = _ref => {
  let {
    isPriceDiscounted,
    hasPriceRange
  } = _ref;
  return `${isPriceDiscounted ? "discounted" : "fullprice"}~${hasPriceRange ? "range" : "no range"}`;
};
const getSeason = _ref2 => {
  let {
    season,
    origSeason,
    origYear
  } = _ref2;
  if (season !== undefined) {
    return season;
  }
  return origSeason && origYear ? `${origSeason} ${origYear}` : null;
};
class Product$1 extends Data$1 {
  // given product is a @arcteryx/data-models::Product
  constructor(product) {
    var _a;
    super();
    this._model = Object.assign(Object.assign({}, this._model), {
      productInfo: {
        // @since 1.0.2
        modelNumber: product.id,
        sku: product.sku || null,
        name: product.analyticsName,
        season: getSeason(product),
        // @since 1.2.0
        articleNumber: product.selectedColour && product.selectedColour.label ? `${product === null || product === void 0 ? void 0 : product.id}-${(_a = product === null || product === void 0 ? void 0 : product.selectedColour) === null || _a === void 0 ? void 0 : _a.label.replace(/\s+/g, "").toLowerCase()}` : null
      },
      price: {
        // @since 1.0.1
        sellingPrice: product.discountPrice || product.price,
        priceType: getPriceType(product),
        currency: product.currencyCode,
        // Not implemented yet
        tax: null
      }
    });
  }
}

const adaptJobData = data => {
  var _a, _b, _c, _d;
  return {
    jobID: data.id,
    jobTitle: data.text,
    jobLocation: ((_a = data.categories) === null || _a === void 0 ? void 0 : _a.location) || null,
    jobTeam: ((_b = data.categories) === null || _b === void 0 ? void 0 : _b.team) || null,
    jobWorkType: ((_c = data.categories) === null || _c === void 0 ? void 0 : _c.commitment) || null,
    jobDepartment: ((_d = data.categories) === null || _d === void 0 ? void 0 : _d.department) || null
  };
};
class Job extends Data$1 {
  constructor(job) {
    super();
    this._model = Object.assign(Object.assign({}, this._model), {
      jobInfo: Object.assign({}, adaptJobData(job))
    });
  }
}

const filterStockSKUs = product => {
  var _a;
  const inStockSKU = [];
  const outOfStockSKU = [];
  (_a = product.variants) === null || _a === void 0 ? void 0 : _a.forEach(variant => {
    var _a;
    if (((_a = product.selectedColour) === null || _a === void 0 ? void 0 : _a.value) === variant.colourId) {
      if (variant.inventory > 0) {
        inStockSKU.push(variant.id);
      } else {
        outOfStockSKU.push(variant.id);
      }
    }
  });
  return {
    inStockSKU,
    outOfStockSKU
  };
};
class CatalogProduct extends Product$1 {
  constructor(product) {
    let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    super(product);
    const {
      quantity,
      isFirstProduct,
      bvRating
    } = options;
    this._model = Object.assign(Object.assign({}, this._model), {
      bvRating,
      productInfo: Object.assign(Object.assign(Object.assign({}, this._model.productInfo), {
        isFirstProduct
      }), filterStockSKUs(product)),
      quantity
    });
  }
}

/**
 *
 * @param {object} colourOptions attribute from product data model { options: [], selectedColour}
 * @returns {object} {
 *  imageType: "on-model", "on-form" or "olapic"
 *  imagePerspective: "Atom LT Hoody Front View" (only applicatble to on-model imageType)
 * }
 */
const adaptProductImage = colourOptions => {
  var _a, _b;
  // selectedColour - current colour selected
  const selectedColour = colourOptions === null || colourOptions === void 0 ? void 0 : colourOptions.selected;
  // Find selected colour option
  const selectedColourOption = (colourOptions === null || colourOptions === void 0 ? void 0 : colourOptions.options.find(colour => (colour === null || colour === void 0 ? void 0 : colour.value) === selectedColour)) || {};
  // selectedImageUrl e.g: https://images.arcteryx.com/details/1350x1710/Alpha-SV-Jacket-Vitality-Front-View.jpg
  const selectedImageUrl = ((_a = selectedColourOption === null || selectedColourOption === void 0 ? void 0 : selectedColourOption.image) === null || _a === void 0 ? void 0 : _a.url) || ((_b = selectedColourOption === null || selectedColourOption === void 0 ? void 0 : selectedColourOption.image) === null || _b === void 0 ? void 0 : _b.pathname) || "";
  /**
   * Format the selectedImageUrl
   * */
  // imageUrlAfterLastSlash e.g: Alpha-SV-Jacket-Vitality-Front-View.jpg
  const imageUrlAfterLastSlash = selectedImageUrl === null || selectedImageUrl === void 0 ? void 0 : selectedImageUrl.substring((selectedImageUrl === null || selectedImageUrl === void 0 ? void 0 : selectedImageUrl.lastIndexOf("/")) + 1);
  // imageUrlBeforeLastPeriod: Alpha-SV-Jacket-Vitality-Front-View
  const imageUrlBeforeLastPeriod = imageUrlAfterLastSlash.includes(".") ? imageUrlAfterLastSlash === null || imageUrlAfterLastSlash === void 0 ? void 0 : imageUrlAfterLastSlash.slice(0, imageUrlAfterLastSlash === null || imageUrlAfterLastSlash === void 0 ? void 0 : imageUrlAfterLastSlash.lastIndexOf(".")) : imageUrlAfterLastSlash;
  /**
   * imageInfo attributes
   */
  // imageType - either on-model or on-form (off-model)
  const imageType = imageUrlBeforeLastPeriod.includes("Front") ? "on-model" : "on-form";
  // imagePerspective e.g: Alpha SV Jacket Vitality Front View
  const imagePerspective = imageType === "on-model" ? imageUrlBeforeLastPeriod === null || imageUrlBeforeLastPeriod === void 0 ? void 0 : imageUrlBeforeLastPeriod.replace(/-/g, " ") : null;
  return {
    imageType,
    imagePerspective
  };
};

const capitalize = string => string.replace(/(^\w|\s\w)(\S*)/g, (_, m1, m2) => m1.toUpperCase() + m2.toLowerCase());

/* May need to add more types that need to be mapped */
const categoryMap = {
  availability: "Availability",
  base_color: "Colour",
  base_colour: "Colour",
  base_colours: "Colour",
  categories: "Shop By",
  collection: "Collections",
  collections: "Collections",
  gender: "Gender",
  genders: "Gender",
  intended_use: "Intended Use",
  materials: "Properties",
  size: "Size",
  size_men: "Size",
  size_women: "Size",
  sort: "Sort",
  sub_categories: "Properties",
  subcategories: "Properties",
  use: "Intended Use",
  activity: "Activity"
};

const formatRefinementValue = value => {
  let newValue = (value === null || value === void 0 ? void 0 : value.replace("catalog01_", "")) || value;
  if (newValue.includes("inventory") && newValue.includes(">1")) {
    newValue = "In stock";
  }
  return sizeMap[newValue] || capitalize(newValue);
};

const sizeMap = {
  xxs: "XXS",
  "2xs": "2XS",
  xs: "XS",
  s2dm: "S-M",
  l2dxl: "L-XL",
  xl: "XL",
  xxl: "XXL",
  "2xl": "2XL",
  "2x": "2X",
  xxxl: "XXXL",
  "3xl": "3XL",
  one20size: "One Size",
  ns: "NS",
  srt: "SRT",
  reg: "REG",
  tall: "TALL"
};

// For ListingViewed and ListingItemClicked Events
class ListingProduct extends Product$1 {
  constructor(product, index) {
    let options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
    super(product);
    this.index = index;
    const {
      imageLocation
    } = options;
    this._model = Object.assign(Object.assign({}, this._model), {
      itemPosition: index + 1,
      rating: {
        ratingCountAndAverage: (product === null || product === void 0 ? void 0 : product.ratingCount) && (product === null || product === void 0 ? void 0 : product.ratingAverage) ? `${product.ratingCount}:${product.ratingAverage}` : ""
      },
      imageInfo: Object.assign(Object.assign({}, adaptProductImage(product === null || product === void 0 ? void 0 : product.colourOptions)), {
        imageLocation
      })
    });
  }
}

class CartProduct extends Product$1 {
  constructor(product) {
    let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    super(product);
    // TODO: We need to choose a default value for quantity, I put 0 but maybe it should be 1?
    const {
      quantity = 0,
      isFirstProduct,
      tax,
      shippingDetails = {},
      fulfillmentDetails = {}
    } = options;
    let sellingPriceDiscount;
    if (product.promoDiscount) {
      const price = product.discountPrice ? product.discountPrice : product.price;
      sellingPriceDiscount = price ? Number((price * quantity - product.promoDiscount).toFixed(2)) : 0;
    } else {
      sellingPriceDiscount = product.discountPrice ? Number((product.discountPrice * quantity).toFixed(2)) : 0;
    }
    const sellingPrice = product.price ? Number((product.price * quantity).toFixed(2)) : 0;
    const {
      shippingMethod,
      shippingCost,
      shippingAddress
    } = shippingDetails;
    const {
      method,
      storeID
    } = fulfillmentDetails;
    this._model = Object.assign(Object.assign({}, this._model), {
      price: Object.assign(Object.assign({}, this._model.price), {
        sellingPrice: sellingPriceDiscount || sellingPrice,
        tax: tax ? Number(tax.toFixed(2)) : null
      }),
      productInfo: Object.assign(Object.assign({}, this._model.productInfo), {
        isFirstProduct,
        shippingMethod,
        shippingCost,
        shippingAddress: {
          country: shippingAddress === null || shippingAddress === void 0 ? void 0 : shippingAddress.country,
          postalCode: shippingAddress === null || shippingAddress === void 0 ? void 0 : shippingAddress.postalCode
        },
        fulfillment: {
          method,
          storeID
        }
      }),
      quantity
    });
  }
}

class OrderedProduct extends CartProduct {
  constructor(product, options) {
    super(product);
    const {
      promo,
      tax,
      shippingMethod,
      shippingCost,
      shippingAddress,
      fulfillment
    } = options;
    let sellingPriceDiscount;
    if (product.promoDiscount) {
      const price = product.discountPrice ? product.discountPrice : product.price;
      sellingPriceDiscount = price ? Number((price * product.quantity - product.promoDiscount).toFixed(2)) : 0;
    } else {
      sellingPriceDiscount = product.discountPrice ? Number((product.discountPrice * product.quantity).toFixed(2)) : 0;
    }
    const sellingPrice = product.price ? Number((product.price * product.quantity).toFixed(2)) : 0;
    this._model = Object.assign(Object.assign({}, this._model), {
      price: Object.assign(Object.assign({}, this._model.price), {
        sellingPrice: sellingPriceDiscount || sellingPrice,
        tax: tax ? Number(tax.toFixed(2)) : null
      }),
      quantity: product === null || product === void 0 ? void 0 : product.quantity,
      voucherDiscount: {
        orderLevelDiscountAmount: (promo === null || promo === void 0 ? void 0 : promo.orderLevelDiscountAmount) || null,
        orderLevelDiscountCode: (promo === null || promo === void 0 ? void 0 : promo.orderLevelDiscountCode) || null,
        productLevelDiscountAmount: (promo === null || promo === void 0 ? void 0 : promo.productLevelDiscountAmount) || null,
        productLevelDiscountCode: (promo === null || promo === void 0 ? void 0 : promo.productLevelDiscountCode) || null
      },
      shippingMethod,
      shippingCost,
      shippingAddress,
      fulfillment
    });
  }
}

class OrderConfirmation extends Data$1 {
  constructor(products, orderInfo) {
    super();
    const {
      payment,
      total,
      transactionId
    } = orderInfo;
    this._model = Object.assign(Object.assign({}, this._model), {
      transaction: {
        item: products,
        payment,
        total,
        transactionID: transactionId
      }
    });
  }
}

class RecommendedProduct extends CatalogProduct {
  constructor(product) {
    let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    super(product);
    const {
      market,
      recommendationParentProductID,
      recommendationZone,
      recommendationPosition,
      recommendationAlgorithm,
      imageLocation,
      wid,
      wrid,
      wtype
    } = options;
    this._model = Object.assign(Object.assign({}, this._model), {
      market,
      imageInfo: Object.assign(Object.assign({}, adaptProductImage(product === null || product === void 0 ? void 0 : product.colourOptions)), {
        imageLocation
      }),
      recommendationParentProductID,
      recommendationZone,
      recommendationPosition,
      recommendationAlgorithm,
      wid,
      wrid,
      wtype
    });
  }
}

class Refinements extends Data$1 {
  constructor(filter) {
    super();
    this._model = Object.assign(Object.assign({}, this._model), {
      refinementValue: formatRefinementValue(filter.id),
      refinementType: categoryMap[filter.catId] || filter.catId
    });
  }
}

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
function __awaiter(thisArg, _arguments, P, generator) {
  function adopt(value) {
    return value instanceof P ? value : new P(function (resolve) {
      resolve(value);
    });
  }
  return new (P || (P = Promise))(function (resolve, reject) {
    function fulfilled(value) {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    }
    function rejected(value) {
      try {
        step(generator["throw"](value));
      } catch (e) {
        reject(e);
      }
    }
    function step(result) {
      result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected);
    }
    step((generator = generator.apply(thisArg, _arguments || [])).next());
  });
}

const defaultUserData = {
  custGroupID: "anonymous",
  gender: "",
  signInMethod: "guest",
  custKey: "",
  loginStatus: "Logged out",
  userType: "Anonymous",
  hashedId: ""
};
const getHashId = key => key ? Hash.sha256().update(key).digest("hex") : "";
class User extends Data$1 {
  constructor(unadaptedUser) {
    super();
    this._model = Object.assign(Object.assign(Object.assign({}, defaultUserData), {
      hashedId: getHashId(unadaptedUser === null || unadaptedUser === void 0 ? void 0 : unadaptedUser.email)
    }), this.adapt(unadaptedUser));
  }
  adapt(user) {
    return user ? {
      loginStatus: "Logged in",
      signInMethod: "regular-signin"
    } : {};
  }
}
class ForgeRockUser extends User {
  adapt(user) {
    let adapted = super.adapt(user);
    if (user) {
      adapted = Object.assign(Object.assign({}, adapted), {
        custGroupID: user.groupId,
        userType: "",
        custKey: user.uuid,
        email: user.email,
        country: user.country,
        language: user.language,
        market: user.market
      });
    }
    return adapted;
  }
  static $create() {
    var _a;
    let customer;
    try {
      customer = JSON.parse((_a = getForgeRockUser()) !== null && _a !== void 0 ? _a : "");
    } catch (err) {
      customer = undefined;
    }
    return new ForgeRockUser(customer);
  }
}
// TODO: Remove this class once the legacy user is removed from account-pages
class LegacyUser extends User {
  adapt(user) {
    var _a, _b;
    let adapted = super.adapt(user);
    if (user) {
      adapted = Object.assign(Object.assign({}, adapted), {
        custGroupID: user.isPro ? `d2x:pro:standard-${(_a = user.orderType) === null || _a === void 0 ? void 0 : _a.replace("PRO-ECOM", "")}` : "regular",
        userType: "",
        email: user.email,
        uuid: (_b = user.uuid) !== null && _b !== void 0 ? _b : ""
      });
    }
    return adapted;
  }
  static $create() {
    return __awaiter(this, void 0, void 0, function* () {
      let customer;
      try {
        customer = yield getCustomer();
      } catch (err) {
        customer = undefined;
      }
      return new LegacyUser(customer);
    });
  }
}

class ReviewedProduct extends Product$1 {
  constructor(product) {
    let ratingCountAndAverage = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    let ratingGiven = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
    super(product);
    this._model = Object.assign(Object.assign({}, this._model), {
      rating: {
        ratingCountAndAverage,
        ratingGiven
      }
    });
  }
}

class CompareProduct extends Product$1 {
  constructor(product) {
    let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    super(product);
    const {
      itemPosition
    } = options;
    this._model = Object.assign(Object.assign({}, this._model), {
      itemPosition
    });
  }
}

class Link extends Data$1 {
  constructor(link) {
    super(link);
    this._model = Object.assign(Object.assign({}, this._model), {
      linkType: link.type || "",
      linkId: link.id || "",
      linkText: link.text || "",
      linkDestination: link.url || "" // the URL of the link clicked
    });
  }
}

class Coupon$1 extends Data$1 {
  constructor(coupon) {
    super();
    this._model = Object.assign(Object.assign({}, this._model), {
      voucherDiscount: {
        discountCode: coupon.promoCode,
        promoCodeMessage: coupon.promoCodeMessage || '',
        infoMessage: coupon.infoMessage || ''
      }
    });
  }
}

class EmailSubscription extends Data$1 {
  constructor() {
    let {
      formType = "",
      name = "",
      email = "",
      gender = ""
    } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    super();
    this._model = Object.assign(Object.assign({}, this._model), {
      formAuthor: name,
      formType,
      user: {
        gender,
        hashedId: getHashId(email)
      }
    });
  }
}
class EmailSubscriptionLoad extends Data$1 {
  constructor() {
    let {
      formType = "",
      name = ""
    } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    super();
    this._model = Object.assign(Object.assign({}, this._model), {
      formAuthor: name,
      formType
    });
  }
}

var Data = /*#__PURE__*/Object.freeze({
    __proto__: null,
    Page: Page,
    Product: Product$1,
    Job: Job,
    CatalogProduct: CatalogProduct,
    ListingProduct: ListingProduct,
    OrderedProduct: OrderedProduct,
    OrderConfirmation: OrderConfirmation,
    RecommendedProduct: RecommendedProduct,
    Refinements: Refinements,
    ForgeRockUser: ForgeRockUser,
    LegacyUser: LegacyUser,
    getHashId: getHashId,
    ReviewedProduct: ReviewedProduct,
    CartProduct: CartProduct,
    CompareProduct: CompareProduct,
    Link: Link,
    Coupon: Coupon$1,
    EmailSubscription: EmailSubscription,
    EmailSubscriptionLoad: EmailSubscriptionLoad
});

const General = {
  /**
   * Push this event when a content page loads (articles, blog posts or other content pages).
   */
  contentLoaded() {
    pushEvent("Content Loaded");
  },
  /**
   * Push this event when a content page is liked (articles, blog posts or other content pages).
   */
  contentLiked() {
    pushEvent("Content Liked");
  },
  /**
   * Push this event when a list of contents is displayed to the user. This can be during onsite searches or on pages that display a list of contents (like Time to Play).
   */
  contentListingDisplayed() {
    pushEvent("Content Listing Displayed");
  },
  /**
   * Push this event when the user clicks on an item of a content list (either on onsite search results or content listing pages).
   */
  contentListingItemClicked() {
    pushEvent("Content Listing Item Clicked");
  },
  /**
   * @since 1.7.0
   * Push this event when a list of products loads. This can happen on level 2 and level 3 category pages or during onsite search results.
   * @productData - A DataLayer.Data.ListingProduct object
   */
  listingViewed() {
    let productList = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
    let refinements = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
    let searchTermEntered = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
    const refinementsModel = refinements === null || refinements === void 0 ? void 0 : refinements.map(refinement => refinement.model);
    const productListModel = productList === null || productList === void 0 ? void 0 : productList.map(product => product.model);
    pushEvent("Listing Viewed", {
      listing: {
        listingParams: {
          refinements: refinementsModel || null,
          searchInfo: {
            searchTermEntered
          }
        },
        listingResults: {
          item: productListModel || null,
          resultsCount: productList === null || productList === void 0 ? void 0 : productList.length
        }
      }
    });
  },
  /**
   * @since 1.7.0
   * Push this event when users click on items in product listings.
   * @productData - A DataLayer.Data.ListingProduct object
   */
  listingItemClicked(product) {
    pushEvent("Listing Item Clicked", {
      listing: {
        listingResults: {
          item: [product === null || product === void 0 ? void 0 : product.model]
        }
      }
    });
  },
  /**
   * @since 1.55.13
   * Push this event when a list of jobs loads
   * @jobList - An array of DataLayer.Data.Job object
   * @refinementList - An array of Refinements objes { refinementType, refinementValue }
   */
  jobListingDisplayed() {
    let jobList = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
    let refinementList = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
    const jobListModel = jobList === null || jobList === void 0 ? void 0 : jobList.map(job => job.model);
    pushEvent("Job Listing Displayed", {
      jobListing: {
        listingDisplayedParams: {
          refinements: refinementList
        },
        job: jobListModel,
        resultsCount: jobList.length
      }
    });
  },
  /**
   * @since 1.34.0
   * Push to the data layer after one second of inactivity once the user has stopped typing in the search box. Push this along with the corresponding Listing Viewed and Content Listing Displayed events.
   */
  onsiteSearchPerformed() {
    let searchTermEntered = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : "";
    let searchTermSuggested = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : "";
    let staticTerm = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : "";
    let termCount = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : "";
    let terms = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : [];
    pushEvent("Onsite Search Performed", {
      onsiteSearch: {
        keyword: {
          searchTermEntered,
          searchTermSuggested,
          staticTerm
        },
        suggestions: {
          termCount,
          terms
        }
      }
    });
  },
  /**
   *
   */
  quickResultClicked() {
    pushEvent("Quick Result Clicked");
  },
  /**
   * @since 1.0.1
   * Push this event when a new page loads. Push it after any other events that happen on that page are pushed (e.g. Order Placed, Listing Viewed, User Detected, Product Viewed)
   * @param {any} pageData - A DataLayer.Data.Page object
   */
  pageLoaded() {
    let pageData = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
    let market = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : "";
    const page = pageData || Page.$createFromUrl(null, market);
    pushEvent("Page Loaded", {
      currency: page.currency,
      page: page.model
    });
  },
  /**
   * Push this event when a user successfully registers to the site. If this event is confirmed during a new page load, push it before the Page Loaded event.
   */
  userRegistered() {
    let userData = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {
      model: {}
    };
    pushEvent("User Registered", {
      user: userData.model
    });
  },
  /**
   * Push this event when a user successfully signs in.
   */
  userSignedIn() {
    let userData = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {
      model: {}
    };
    pushEvent("User Signed In", {
      user: userData.model
    });
  },
  /**
   * @since 1.5.0
   * The User Detected event should be always pushed to the appEventData object before the Page Loaded event.
   */
  userDetected() {
    let userData = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {
      model: {}
    };
    pushEvent("User Detected", {
      user: userData.model
    });
  },
  /**
   * Push this event when a user successfully subscribes to a newsletter.
   * @param emailSubscription { EmailSubscription | null }
   */
  emailSubscriptionCompleted() {
    let emailSubscription = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
    pushEvent("Email Subscription Completed", emailSubscription === null || emailSubscription === void 0 ? void 0 : emailSubscription.model);
  },
  /**
   * Push this event when users start the email subscription form.
   * @param emailSubscriptionLoad { EmailSubscriptionLoad | null }
   */
  emailSubscriptionStarted() {
    let emailSubscriptionLoad = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
    pushEvent("Email Subscription Started", emailSubscriptionLoad === null || emailSubscriptionLoad === void 0 ? void 0 : emailSubscriptionLoad.model);
  },
  /**
   * Push this event whenever an error message is presented to the client. This includes all the event types listed in the errorType field examples.
   */
  errorMessagePresented(error) {
    pushEvent("Error Message Presented", {
      error: {
        message: error === null || error === void 0 ? void 0 : error.message,
        type: error === null || error === void 0 ? void 0 : error.type
      }
    });
  },
  /**
   * @since 1.16.0
   * Push this event when users click on links or buttons.
   */
  linkClicked(link) {
    pushEvent("Link Clicked", {
      linkInfo: link === null || link === void 0 ? void 0 : link.model
    });
  },
  /**
   * @since 1.15.0
   * Push this event when users click on links on the navigation bar.
   */
  navigationLinkClicked(link) {
    pushEvent("Navigation Link Clicked", {
      linkInfo: link === null || link === void 0 ? void 0 : link.model
    });
  },
  /**
   * @since 1.15.0
   * Push this event when users click on links on the body of the page. This is particularly important for level 1 category pages (e.g. Man, Woman or WMN Collection).
   */
  bodyNavigationLinkClicked(link) {
    pushEvent("Body Navigation Link Clicked", {
      linkInfo: link === null || link === void 0 ? void 0 : link.model
    });
  },
  /**
   * Push this event when product comparison pages load. It has to be pushed before the Page Loaded event.
   */
  productComparisonViewed() {
    pushEvent("Product Comparison Viewed");
  },
  /**
   * @since 1.15.0
   * Push this event when users click on items in product listings.
   */
  comparisonItemClicked(product) {
    pushEvent("Comparison Item Clicked", {
      product: [product === null || product === void 0 ? void 0 : product.model]
    });
  },
  /**
   * @since 1.19.1
   * Push this event when a form submission fails. This includes all the forms on the website.
   * @param {any} formData
   */
  formSubmissionFailed(formData) {
    pushEvent("Form Submission Failed", {
      form: {
        formName: formData === null || formData === void 0 ? void 0 : formData.formName,
        formField: formData === null || formData === void 0 ? void 0 : formData.formField
      }
    });
  },
  /**
   * @since 1.30.0
   * Push this event when promotions are clicked on the website.
   * (Merchandising Banners)
   */
  promotionClicked(promotionID, experienceId, campaignId, userGroup) {
    pushEvent("Promotion Clicked", {
      promotions: [{
        promotionID,
        experienceId,
        campaignId,
        userGroup
      }]
    });
  },
  /**
   * @since 1.31.1
   * Push this event when promotions are displayed to the user. If the promotions exist on the page push this event before the Page Loaded event.
   * (Merchandising Banners)
   */
  promotionViewed(promotionID) {
    pushEvent("Promotion Viewed", {
      promotions: [{
        promotionID
      }]
    });
  },
  payNowClicked() {
    let paymentMethod = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
    pushEvent("Pay Now Clicked", {
      payment: [{
        paymentMethod
      }]
    });
  },
  /**
   * Push this event when users successfully entry a discount coupon.
   */
  couponEntrySucceeded() {
    pushEvent("Coupon Entry Succeeded");
  },
  /**
   * Push this event when users start playing videos.
   */
  videoPlayed(mediaData) {
    pushEvent("Video Played", {
      media: {
        mediaName: mediaData.mediaName,
        mediaLocation: mediaData.mediaLocation
      }
    });
  },
  /**
   * Push this event when videos are finished playing.
   */
  videoCompleted(mediaData) {
    pushEvent("Video Completed", {
      media: {
        mediaName: mediaData.mediaName,
        mediaLocation: mediaData.mediaLocation
      }
    });
  }
};

/**
 * @param cart {Cart}
 * @returns SalesForceCartItem[]
 */
const cartToSalesForceCart = cart => {
  var _a;
  const cartItems = (_a = cart === null || cart === void 0 ? void 0 : cart.products) === null || _a === void 0 ? void 0 : _a.map(_ref => {
    let {
      quantity,
      price,
      sku,
      id
    } = _ref;
    return {
      quantity,
      price,
      item: id,
      unique_id: sku
    };
  });
  return {
    cartId: cart === null || cart === void 0 ? void 0 : cart.id,
    cartItems
  };
};

const filterSelectedSKUs = (productModelInfo, skus) => skus.filter(selectedSKU => productModelInfo.sku === selectedSKU);
const Product = {
  /**
   * Push this event whenever the flyin cart is opened
   */
  cartPopupDisplayed(cartItems) {
    let trigger = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : "";
    let cart = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : [];
    const cartProducts = cartItems === null || cartItems === void 0 ? void 0 : cartItems.map(cartProduct => cartProduct.model);
    pushEvent("Cart Popup Displayed", {
      popupInfo: {
        trigger
      },
      cart: {
        item: cartProducts
      },
      salesForceCart: cartToSalesForceCart(cart)
    });
  },
  /**
   * Push this event whenever a user adds a product to the basket.
   * @param {CatalogProduct} product
   * @param {Cart} cart
   */
  productAdded(product, cart) {
    pushEvent("Product Added", {
      product: [product.model],
      salesForceCart: cartToSalesForceCart(cart)
    });
  },
  /*
   * Push this event whenever a user removes a product from the basket.
   * @param {CatalogProduct} product
   * @param {Cart} cart
   */
  productRemoved(product, cart) {
    pushEvent("Product Removed", {
      product: [product.model],
      salesForceCart: cartToSalesForceCart(cart)
    });
  },
  /**
   * @since 1.0.1
   * Push this event when a product detail page loads. This event has to be pushed before the Page Loaded event.
   * @productData - A DataLayer.Data.Product object
   */
  productViewed(_ref, action) {
    let {
      model
    } = _ref;
    let productModelInfo = Object.assign({}, model.productInfo);
    if (action === "Color Selected" && productModelInfo.inStockSKU && productModelInfo.inStockSKU.length > 0) {
      productModelInfo = Object.assign(Object.assign({}, productModelInfo), {
        action,
        sku: productModelInfo.inStockSKU[0]
      });
    }
    pushEvent("Product Viewed", {
      product: [Object.assign(Object.assign({}, model), {
        productInfo: productModelInfo
      })]
    });
  },
  /**
   * Push this event when users start writing a product review on a PDP.
   */
  productReviewStarted(_ref2) {
    let {
      model
    } = _ref2;
    pushEvent("Product Review Started", {
      product: [model]
    });
  },
  /**
   * Push this event when a user successfully submits a product review.
   */
  productReviewCompleted(_ref3) {
    let {
      model
    } = _ref3;
    pushEvent("Rating Given", {
      product: [model]
    });
  },
  /**
   * Push this event when users translates product review on a PDP.
   */
  productReviewTranslated() {
    pushEvent("Translated Reviews");
  },
  /**
   * Push this event when a user clicks on a product review summary to see the reviews and their comments.
   */
  productReviewClicked(_ref4) {
    let {
      model
    } = _ref4;
    pushEvent("Product Review Clicked", {
      product: [model]
    });
  },
  /**
   * Push this event when users click on products recommended on PDPs, Cart pages and Category Pages.
   */
  relatedProductClicked(relatedProduct) {
    const productModels = relatedProduct === null || relatedProduct === void 0 ? void 0 : relatedProduct.map(product => product.model);
    pushEvent("Related Product Clicked", {
      relatedProducts: {
        item: productModels
      }
    });
  },
  /**
   * Push this event when recommended products are displayed on PDPs, Cart pages and Category Pages.
   */
  relatedProductsViewed(relatedProducts) {
    const productModels = relatedProducts === null || relatedProducts === void 0 ? void 0 : relatedProducts.map(product => product.model);
    pushEvent("Related Products Viewed", {
      relatedProducts: {
        item: productModels
      }
    });
  },
  /**
   * @since 1.0.1
   * Push this event when users click on PDP sections (i.e. Features, Details, Care and Benefits)
   *
   * Spec describing sections and when to fire this event:
   * https://docs.google.com/document/d/1xUujliIfX4TuzlugaoR1LPZ36WQp16dLMa_cBINnICU
   */
  pdpSectionViewed(sectionName) {
    pushEvent("PDP Section Viewed", {
      page: {
        virtualPageName: sectionName
      }
    });
  },
  /**
   * Push this event when users select a product size on the PDP.
   */
  productSizeSelected(productData, stockMessage) {
    const productModel = productData.model;
    const productModelInfo = productModel.productInfo;
    productModelInfo.stockMessage = stockMessage;
    productModelInfo.inStockSKU = filterSelectedSKUs(productModelInfo, productModelInfo.inStockSKU);
    productModelInfo.outOfStockSKU = filterSelectedSKUs(productModelInfo, productModelInfo.outOfStockSKU);
    pushEvent("Product Size Selected", {
      product: [productModel]
    });
  },
  /**
   * Push this event when users select a Locally dealer on the PDP.
   */
  brandStoreClicked(storeName) {
    pushEvent("Brand Store Clicked", {
      storeInfo: {
        storeName
      }
    });
  },
  /**
   * Push this event when the Virtual Advisor Callout component is loaded
   */
  calloutInteraction(interaction) {
    pushEvent("Callout Slide Interaction", {
      calloutInfo: {
        calloutInteraction: interaction
      }
    });
  },
  /**
   * Push this event when the Virtual Advisor Callout component is loaded
   */
  calloutLoaded() {
    this.calloutInteraction("virtual advisor:impression");
  },
  /**
   * Push this event when the Virtual Advisor Callout component is clicked
   */
  calloutClicked() {
    this.calloutInteraction("virtual advisor:click");
  },
  /**
   * Push this event when the Virtual Advisor Callout component is closed
   */
  calloutClosed() {
    this.calloutInteraction("virtual advisor:close");
  },
  /**
   * Push this event when the Virtual Advisor Callout component CTA is clicked
   */
  calloutCtaClicked() {
    this.calloutInteraction("virtual advisor:cta click");
  },
  /**
   * Push this event when the Virtual Advisor Callout component is closed
   */
  productImageClicked(imageInfo) {
    pushEvent("Clicked PDP Image", {
      imageInfo
    });
  },
  /**
   * Send the cartId and identifying user info to SalesForce so can create an abandon cart email with a link
   * to that cartId
   * @param obj {Object}
   * @param obj.user {unknown}
   * @param obj.cartId {string}
   */
  usersCartIdUpdated(_ref5) {
    let {
      user,
      cartId
    } = _ref5;
    pushEvent("Users Cart Id Updated", {
      user,
      cartId
    });
  },
  /**
   * Push this event when a user initiates the stock alert form by clicking ‘Notify Me’
   */
  stockAlertFormStarted(data) {
    pushEvent("Stock Alert Form Started", {
      product: [data]
    });
  },
  /**
   * Push this event when a user successfully submits a stock alert form.
   */
  stockAlertFormSucceeded(data) {
    pushEvent("Stock Alert Form Succeeded", {
      product: [data]
    });
  }
};

const CartCheckout = {
  /**
   * @since 1.0.1
   * Push this event when the user loads the cart page, before the Page Loaded event.
   * @productDataArray - An array of DataLayer.Data.Product objects
   */
  cartViewed() {
    let productDataArray = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
    let cart = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    const productModels = productDataArray === null || productDataArray === void 0 ? void 0 : productDataArray.map(product => product.model);
    pushEvent("Cart Viewed", {
      cart: {
        item: productModels
      },
      salesForceCart: cartToSalesForceCart(cart)
    });
  },
  cartShared() {
    let productDataArray = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
    const productModels = productDataArray === null || productDataArray === void 0 ? void 0 : productDataArray.map(product => product.model);
    pushEvent("Cart Shared", {
      cart: {
        item: productModels
      }
    });
  },
  /**
   * Push this event when the user starts the checkout process. If this takes place when a page loads, push the event before the Page Loaded event.
   */
  checkoutStarted(checkoutMethod) {
    let productDataArray = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
    const productModels = productDataArray === null || productDataArray === void 0 ? void 0 : productDataArray.map(product => product.model);
    pushEvent("Checkout Started", {
      checkoutMethod,
      cart: {
        item: productModels
      }
    });
  },
  // Need to implement
  /**
   * When the user starts a new step on the checkout process (e.g. Payment details, Billing details and Address)
   */
  checkoutStepEncountered(checkoutStep) {
    let productDataArray = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
    const productModels = productDataArray === null || productDataArray === void 0 ? void 0 : productDataArray.map(product => product.model);
    pushEvent("Checkout Step Encountered", {
      eventDetails: {
        checkoutStep
      },
      product: productModels
    });
  },
  orderPlaced() {
    let orderInfo = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {
      _model: {
        transaction: null
      }
    };
    const {
      transaction
    } = orderInfo._model;
    pushEvent("Order Placed", {
      transaction
    });
  }
};

const Compare = {
  productCompareViewed(productList, productSlugs) {
    const productListModels = productList === null || productList === void 0 ? void 0 : productList.map(product => product.model);
    pushEvent("Product Comparison Viewed", {
      product: productListModels,
      productComparisonList: productSlugs
    });
  },
  productCompareClicked(product) {
    pushEvent("Comparison Item Clicked", {
      product: [product.model]
    });
  }
};

const Coupon = {
  couponEntrySucceeded(coupon) {
    pushEvent("Coupon Entry Succeeded", coupon.model);
  },
  couponEntryFailed(coupon) {
    pushEvent("Coupon Entry Failed", coupon.model);
  }
};

const Store = {
  /**
   * Push this event when a user clicks on a store in the store locator dropdown.
   * @param {string} interaction - The type of interaction that occurred.
   * @param {string} storeName - The name of the store.
   * @param {boolean} availableInStore - Whether the product is available in the store.
   */
  brandStoreClicks() {
    let interaction = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : "";
    let storeName = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : "";
    let availableInStore = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
    pushEvent("Brand Store Clicked", {
      storeInfo: {
        interaction,
        storeName,
        availableInStore
      }
    });
  },
  /**
   * Push this event when a user views stores in the pickup in-store modal on PDP and shipping page
   * @param {string} defaultPostalCode  - The default postal code of the store.
   * @param {array} storeResultsArr  - array of store objects
   * storeResultsArr item model:
   * {
   *     "itemPosition": "20", // Position of the item in array
   *     "inStock": "<true|false>", // Boolean value indicating whether the store has item in stock
   *     "storeInfo": {
   *       "storeID": "<storeID>" // Store ID
   *    }
   *   }
   */
  storeListingViewed() {
    let defaultPostalCode = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : "";
    let storeResultsArr = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
    const storeResults = Boolean(storeResultsArr) ? storeResultsArr : [];
    const resultsCount = storeResults === null || storeResults === void 0 ? void 0 : storeResults.length;
    const resultsInStockCount = storeResults === null || storeResults === void 0 ? void 0 : storeResults.filter(store => store.inStock === "true").length;
    const resultsOutOfStockCount = resultsCount - resultsInStockCount;
    pushEvent("Store Listing Viewed", {
      storeInfo: {
        storeParams: {
          searchInfo: {
            defaultPostalCode
          }
        },
        storeResults: {
          item: [...storeResults],
          resultsCount,
          resultsInStockCount,
          resultsOutOfStockCount
        }
      }
    });
  },
  /**
   * Push this event when a user selects a store in locally modal and clicks cta.
   * @param {object} storeResultsArr  - store result object
   * Array of objects with model:
   * {
   *    "itemPosition": "20", // Position of the item in array
   *    "storeInfo": {
   *      "storeID": "<storeID>" // Store ID
   *     }
   *  }
   */
  storeListingItemClicked() {
    let storeResultsArr = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
    pushEvent("Store Listing Item Clicked", {
      storeInfo: {
        storeResults: {
          item: [...storeResultsArr]
        }
      }
    });
  }
};

const Form = {
  /**
   * @param {*} formData Formstack data
   * @param {String} formType Form type
   */
  formSubmissionStarted(formData) {
    let formType = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : "";
    pushEvent("Form Started", {
      form: {
        formName: formData === null || formData === void 0 ? void 0 : formData.name,
        formType
      }
    });
  },
  /**
   * @since 1.19.1
   * Push this event when a form submission fails. This includes all the forms on the website.
   * formField model:
   * {
   *    "formFieldName": "<formFieldName>"
   *    "formFieldError": "<formFieldError>"
   *  }
   * @param {any} formData
   * @param {any} paymentMethod
   */
  formSubmissionFailed(formData) {
    let paymentMethod = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    pushEvent("Form Submission Failed", {
      form: {
        formName: formData === null || formData === void 0 ? void 0 : formData.formName,
        formField: formData === null || formData === void 0 ? void 0 : formData.formField
      },
      payment: [{
        paymentMethod
      }]
    });
  },
  /**
   *
   * @param {Object} formData Formstack data
   * @param {String} formType Form type
   */
  formSubmissionCompleted(formData) {
    let formType = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : "";
    pushEvent("Form Submission Succeeded", {
      form: {
        formName: formData === null || formData === void 0 ? void 0 : formData.name,
        formType
      }
    });
  }
};

const Event = Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, General), Product), CartCheckout), Compare), Coupon), Store), Form);

const DataLayer = {
  setContainer,
  setName,
  reset,
  pushEvent,
  Event,
  Data
};

export { DataLayer as default };
//# sourceMappingURL=index.js.map
